import type { MetaDescriptor } from 'react-router';

// Production origin for canonical URLs, Open Graph, robots.txt and the sitemap.
export const SITE_URL = 'https://shineunuudur.edu.mn';
export const SITE_NAME = 'Шинэ Өнөөдөр Сургууль';
// 1200×630 card shown when a shared page has no image of its own.
export const DEFAULT_SHARE_IMAGE = '/og-image.png';
export const DEFAULT_DESCRIPTION =
  'Шинэ Өнөөдөр Сургууль — Улаанбаатар хотын ерөнхий боловсролын сургууль. Бэлтгэл бүлэг, ерөнхий эрдэм, хөдөлмөр зуслан, дугуйлан секц, тэмцээн уралдаан, сургуулийн мэдээ мэдээлэл.';

/** Title, description, canonical and Open Graph tags for a public page. */
export function pageMeta({
  title,
  description = DEFAULT_DESCRIPTION,
  path,
  sharePath = path,
  image,
  type = 'website',
}: {
  title: string;
  description?: string;
  path: string;
  /** og:url when a shared link should keep its query (e.g. ?tab=register); canonical stays `path`. */
  sharePath?: string;
  image?: string | null;
  type?: 'website' | 'article';
}): MetaDescriptor[] {
  const url = `${SITE_URL}${path}`;
  const fullTitle = title === SITE_NAME ? title : `${title} | ${SITE_NAME}`;
  const imageUrl = absoluteUrl(image ?? DEFAULT_SHARE_IMAGE);
  return [
    { title: fullTitle },
    { name: 'description', content: description },
    { tagName: 'link', rel: 'canonical', href: url },
    { property: 'og:type', content: type },
    { property: 'og:site_name', content: SITE_NAME },
    { property: 'og:locale', content: 'mn_MN' },
    { property: 'og:title', content: fullTitle },
    { property: 'og:description', content: description },
    { property: 'og:url', content: `${SITE_URL}${sharePath}` },
    { property: 'og:image', content: imageUrl },
    ...(image
      ? []
      : [
          { property: 'og:image:width', content: '1200' },
          { property: 'og:image:height', content: '630' },
        ]),
    { name: 'twitter:card', content: 'summary_large_image' },
  ];
}

/** For pages that shouldn't appear in search results (confirmations, 404). */
export function noIndexMeta(title: string): MetaDescriptor[] {
  return [{ title: `${title} | ${SITE_NAME}` }, { name: 'robots', content: 'noindex' }];
}

/** Makes a site-relative image path absolute; leaves full URLs as they are. */
export function absoluteUrl(src?: string | null): string | null {
  if (!src) return null;
  if (/^https?:\/\//.test(src)) return src;
  return `${SITE_URL}${src.startsWith('/') ? '' : '/'}${src}`;
}

/** Plain-text summary of rich HTML content for a meta description. */
export function excerpt(html: string | null | undefined, maxLength = 160): string | null {
  if (!html) return null;
  const text = html
    .replace(/<(script|style)[\s\S]*?<\/\1>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/\s+/g, ' ')
    .trim();
  if (!text) return null;
  if (text.length <= maxLength) return text;
  const cut = text.slice(0, maxLength - 1);
  const lastSpace = cut.lastIndexOf(' ');
  return `${lastSpace > maxLength / 2 ? cut.slice(0, lastSpace) : cut}…`;
}
